/**
 * Bidirectional field mapping between schema paths and component sections/fields
 *
 * Forward mapping: Schema paths → Component sections/fields
 * Reverse mapping: Component sections → Schema IDs
 */

// ============================================================================
// FORWARD MAPPING: Schema → Component
// ============================================================================

export interface SchemaToComponentMapping {
  schemaPath: string; // e.g., "hero_section.header"
  componentSection: string; // e.g., "hero"
  componentField: string; // e.g., "title"
  schemaId: string; // e.g., "hero_section"
}

// Complete mapping dictionary
export const SCHEMA_TO_COMPONENT_MAPPING: SchemaToComponentMapping[] = [
  // Hero Section
  {
    schemaPath: "hero_section.header",
    componentSection: "hero",
    componentField: "title",
    schemaId: "hero_section",
  },
  {
    schemaPath: "hero_section.subhead",
    componentSection: "hero",
    componentField: "description",
    schemaId: "hero_section",
  },
  {
    schemaPath: "hero_section.image",
    componentSection: "hero",
    componentField: "image",
    schemaId: "hero_section",
  },
  {
    schemaPath: "hero_section.book_link_copy",
    componentSection: "hero",
    componentField: "heroBookingCopy",
    schemaId: "hero_section",
  },
  {
    schemaPath: "hero_section.text",
    componentSection: "hero",
    componentField: "text",
    schemaId: "hero_section",
  },
  {
    schemaPath: "hero_section.include_book_now_button",
    componentSection: "hero",
    componentField: "includeBookingButton",
    schemaId: "hero_section",
  },

  // Services
  {
    schemaPath: "our_services.header",
    componentSection: "services",
    componentField: "title",
    schemaId: "our_services",
  },
  {
    schemaPath: "our_services.subtext",
    componentSection: "services",
    componentField: "subtext",
    schemaId: "our_services",
  },
  {
    schemaPath: "our_services.default",
    componentSection: "services",
    componentField: "default",
    schemaId: "our_services",
  },
  {
    schemaPath: "our_services.subtitle",
    componentSection: "services",
    componentField: "subtitle",
    schemaId: "our_services",
  },
  {
    schemaPath: "our_services.menu_note",
    componentSection: "services",
    componentField: "menuNote",
    schemaId: "our_services",
  },

  // Team
  {
    schemaPath: "team.title",
    componentSection: "team",
    componentField: "title",
    schemaId: "team",
  },
  {
    schemaPath: "team.text",
    componentSection: "team",
    componentField: "body",
    schemaId: "team",
  },

  // Service Highlights (optional)
  {
    schemaPath: "service_highlights.highlights",
    componentSection: "highlights",
    componentField: "items",
    schemaId: "service_highlights",
  },

  // Gallery (optional)
  {
    schemaPath: "gallery.title",
    componentSection: "gallery",
    componentField: "title",
    schemaId: "gallery",
  },
  {
    schemaPath: "gallery.items",
    componentSection: "gallery",
    componentField: "items",
    schemaId: "gallery",
  },
  {
    schemaPath: "gallery.grid_layout",
    componentSection: "gallery",
    componentField: "gridLayout",
    schemaId: "gallery",
  },

  // Testimonials (optional)
  {
    schemaPath: "testimonials.title",
    componentSection: "testimonials",
    componentField: "title",
    schemaId: "testimonials",
  },
  {
    schemaPath: "testimonials.subtitle",
    componentSection: "testimonials",
    componentField: "description",
    schemaId: "testimonials",
  },
  {
    schemaPath: "testimonials.text",
    componentSection: "testimonials",
    componentField: "body",
    schemaId: "testimonials",
  },
  {
    schemaPath: "testimonials.testimonials",
    componentSection: "testimonials",
    componentField: "cards",
    schemaId: "testimonials",
  },

  // Contact/Google Maps (optional) - Legacy support
  {
    schemaPath: "google_maps.enabled",
    componentSection: "contact",
    componentField: "showMap",
    schemaId: "google_maps",
  },
  // Find Us Section (REQUIRED)
  {
    schemaPath: "find_us.title",
    componentSection: "contact",
    componentField: "title",
    schemaId: "find_us",
  },
  {
    schemaPath: "find_us.subtitle",
    componentSection: "contact",
    componentField: "subtitle",
    schemaId: "find_us",
  },
  {
    schemaPath: "find_us.google_maps",
    componentSection: "contact",
    componentField: "showMap",
    schemaId: "find_us",
  },

  // Footer/Socials (optional)
  {
    schemaPath: "socials.instagram_url",
    componentSection: "footer",
    componentField: "instagram",
    schemaId: "socials",
  },
  {
    schemaPath: "socials.facebook_url",
    componentSection: "footer",
    componentField: "facebook",
    schemaId: "socials",
  },
  {
    schemaPath: "socials.twitter_url",
    componentSection: "footer",
    componentField: "twitter",
    schemaId: "socials",
  },
  {
    schemaPath: "socials.tiktok_url",
    componentSection: "footer",
    componentField: "tiktok",
    schemaId: "socials",
  },

  // Colors
  {
    schemaPath: "color.primary_color",
    componentSection: "colors",
    componentField: "primary",
    schemaId: "color",
  },
  {
    schemaPath: "color.secondary_color",
    componentSection: "colors",
    componentField: "secondary",
    schemaId: "color",
  },

  // Fonts
  {
    schemaPath: "font.primary_font",
    componentSection: "fonts",
    componentField: "heading",
    schemaId: "font",
  },
  {
    schemaPath: "font.secondary_font",
    componentSection: "fonts",
    componentField: "body",
    schemaId: "font",
  },

  // Company Name
  {
    schemaPath: "company_name",
    componentSection: "general",
    componentField: "company_name",
    schemaId: "company_name",
  },

  // Booking Link (REQUIRED)
  {
    schemaPath: "booking_link.url",
    componentSection: "bookingLink",
    componentField: "link",
    schemaId: "booking_link",
  },
  {
    schemaPath: "booking_link.title",
    componentSection: "bookingLink",
    componentField: "copy",
    schemaId: "booking_link",
  },
  // Also map from hero.heroBookingCopy as fallback
  {
    schemaPath: "booking_link.title",
    componentSection: "hero",
    componentField: "heroBookingCopy",
    schemaId: "booking_link",
  },

  // Banner (optional)
  {
    schemaPath: "banner.title",
    componentSection: "banner",
    componentField: "sectionName",
    schemaId: "banner",
  },
  {
    schemaPath: "banner.content",
    componentSection: "banner",
    componentField: "text",
    schemaId: "banner",
  },
  {
    schemaPath: "banner.text_color",
    componentSection: "banner",
    componentField: "textColor",
    schemaId: "banner",
  },
  {
    schemaPath: "banner.background_color",
    componentSection: "banner",
    componentField: "backgroundColor",
    schemaId: "banner",
  },

  // Announcement Modal (optional)
  {
    schemaPath: "announcement_modal.title",
    componentSection: "announcementBanner",
    componentField: "sectionName",
    schemaId: "announcement_modal",
  },
  {
    schemaPath: "announcement_modal.body",
    componentSection: "announcementBanner",
    componentField: "body",
    schemaId: "announcement_modal",
  },
  {
    schemaPath: "announcement_modal.items",
    componentSection: "announcementBanner",
    componentField: "items",
    schemaId: "announcement_modal",
  },
  {
    schemaPath: "announcement_modal.call_to_action",
    componentSection: "announcementBanner",
    componentField: "callToAction",
    schemaId: "announcement_modal",
  },
];

// ============================================================================
// REVERSE MAPPING: Component → Schema
// ============================================================================

export const COMPONENT_TO_SCHEMA_MAP: Record<string, string> = {
  hero: "hero_section",
  services: "our_services",
  team: "team",
  contact: "find_us", // Maps to find_us schema
  footer: "footer", // May not have schema ID
  bookingLink: "booking_link", // Maps to booking_link schema
  gallery: "gallery",
  testimonials: "testimonials",
  announcementBanner: "announcement_modal", // ✅ FIXED: Correct schema ID
  banner: "banner",
  highlights: "service_highlights",
  colors: "color",
  fonts: "font",
  general: "company_name",
};

// ============================================================================
// FORWARD MAPPING FUNCTIONS: Schema → Component
// ============================================================================

/**
 * Convert schema path to component section and field
 */
export const schemaPathToComponentField = (
  schemaPath: string,
): { section: string; field: string } | null => {
  const mapping = SCHEMA_TO_COMPONENT_MAPPING.find(
    (m) => m.schemaPath === schemaPath,
  );
  if (!mapping) return null;
  return { section: mapping.componentSection, field: mapping.componentField };
};

/**
 * Get component section from schema ID
 */
export const getComponentSectionFromSchemaId = (
  schemaId: string,
): string | null => {
  const mapping = SCHEMA_TO_COMPONENT_MAPPING.find(
    (m) => m.schemaId === schemaId,
  );
  return mapping ? mapping.componentSection : null;
};

/**
 * Get schema ID from schema path (e.g., "hero_section" from "hero_section.header")
 */
export const getSchemaIdFromPath = (schemaPath: string): string => {
  return schemaPath.split(".")[0];
};

// ============================================================================
// REVERSE MAPPING FUNCTIONS: Component → Schema
// ============================================================================

/**
 * Get schema ID from component section key
 */
export const getSchemaIdFromComponentSection = (
  componentSection: string,
): string | null => {
  return COMPONENT_TO_SCHEMA_MAP[componentSection] || null;
};

/**
 * Convert component section and field to schema path
 */
export const componentFieldToSchemaPath = (
  componentSection: string,
  componentField: string,
): string | null => {
  const mapping = SCHEMA_TO_COMPONENT_MAPPING.find(
    (m) =>
      m.componentSection === componentSection &&
      m.componentField === componentField,
  );
  return mapping ? mapping.schemaPath : null;
};

/**
 * Get all missing fields for a specific section
 * Takes a section name and list of missing schema paths, returns the component field names
 */
export const getMissingFieldsForSection = (
  section: string,
  missingFields: string[],
): string[] => {
  return SCHEMA_TO_COMPONENT_MAPPING.filter(
    (m) =>
      m.componentSection === section && missingFields.includes(m.schemaPath),
  ).map((m) => m.componentField);
};

/**
 * Check if a component field should be highlighted based on missing fields
 */
export const isComponentFieldMissing = (
  componentField: string,
  section: string,
  missingFields: string[],
): boolean => {
  const mapping = SCHEMA_TO_COMPONENT_MAPPING.find(
    (m) =>
      m.componentField === componentField && m.componentSection === section,
  );

  if (!mapping) return false;

  return missingFields.includes(mapping.schemaPath);
};
